import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { comics, genreList } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";
import ComicFilters from "@/components/feature/ComicFilters";
import EmptyState from "@/components/feature/EmptyState";
import Pagination, { Breadcrumb } from "@/components/feature/Pagination";
import {
  filterComics,
  paginate,
  PAGE_SIZE,
  type SortKey,
  type StatusKey,
} from "@/utils/comicFilter";

export default function Category() {
  const [params, setParams] = useSearchParams();

  const activeGenres = (params.get("genre") || "")
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean);
  const status = (params.get("status") as StatusKey) || "all";
  const sort = (params.get("sort") as SortKey) || "update";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const filtered = useMemo(
    () => filterComics(comics, { genres: activeGenres, status, sort, keyword: "" }),
    [activeGenres, status, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageList = paginate(filtered, page);
  const featured = useMemo(
    () => [...comics].sort((a, b) => b.favorites - a.favorites).slice(0, 5),
    [],
  );

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setParams(next);
  };

  const toggleGenre = (genre: string) => {
    const set = new Set(activeGenres);
    if (set.has(genre)) set.delete(genre);
    else set.add(genre);
    update({ genre: Array.from(set).join(","), page: null });
  };

  const reset = () => setParams(new URLSearchParams());

  return (
    <main className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "漫画分类" }]} />

      <header className="mt-4 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          漫画分类
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          这里是老司机漫画的完整分类总览。你可以按题材多选筛选，结合更新状态与排序方式，快速找到想看的热血、恋爱、奇幻、悬疑、科幻等各类漫画作品。
        </p>
      </header>

      <ComicFilters
        genres={genreList}
        activeGenres={activeGenres}
        onToggleGenre={toggleGenre}
        status={status}
        onStatusChange={(s) => update({ status: s === "all" ? null : s, page: null })}
        sort={sort}
        onSortChange={(s) => update({ sort: s === "update" ? null : s, page: null })}
        onReset={reset}
      />

      <div className="flex items-center justify-between flex-wrap gap-2 mt-6 mb-5">
        <p className="text-sm text-foreground-500">
          共找到 <strong className="text-primary-600 font-semibold">{filtered.length}</strong> 部漫画
        </p>
        {(activeGenres.length > 0 || status !== "all" || sort !== "update") && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {activeGenres.map((g) => (
              <span
                key={g}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-100 text-primary-700"
              >
                {g}
                <button
                  type="button"
                  aria-label={`移除 ${g}`}
                  onClick={() => toggleGenre(g)}
                  className="cursor-pointer hover:text-primary-900"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {pageList.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageList.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
          <Pagination
            current={Math.min(page, totalPages)}
            total={totalPages}
            onChange={(p) => {
              update({ page: p === 1 ? null : String(p) });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </>
      ) : (
        <EmptyState
          title="没有找到符合条件的漫画"
          desc="试试更换题材标签，或调整排序与连载状态筛选条件，也许会有新的发现。"
          onReset={reset}
          recommendations={featured}
        />
      )}
    </main>
  );
}